/**
 * Monaco diagnostics provider for SOQL queries.
 *
 * Validates queries and marks errors in the editor.
 */

import { parse, extractReferences } from '../../lib/soql-parser/index.js';

/**
 * Validate SOQL query and return Monaco markers.
 *
 * @param {object} model - Monaco editor model
 * @param {object} metadata - Salesforce metadata
 * @returns {Array} Monaco diagnostic markers
 */
export function validateSOQL(model, metadata) {
  const query = model.getValue();
  const markers = [];

  // Parse the query
  const result = parse(query);

  // Add syntax errors
  for (const error of result.errors) {
    markers.push({
      severity: 8, // Error
      startLineNumber: error.line,
      startColumn: error.column + 1,
      endLineNumber: error.line,
      endColumn: error.column + (error.offendingSymbol?.length || 1) + 1,
      message: error.message,
      source: 'SOQL Parser',
    });
  }

  // If syntax is valid, do semantic validation
  if (result.errors.length === 0 && metadata) {
    const semanticMarkers = validateSemantics(query, result.tree, metadata, model);
    markers.push(...semanticMarkers);
  }

  return markers;
}

/**
 * Validate semantic correctness (objects and fields exist).
 */
function validateSemantics(query, tree, metadata, model) {
  const markers = [];

  try {
    const refs = extractReferences(tree);

    // Validate main object
    if (refs.mainObject) {
      const objectExists = metadata.objects && metadata.objects[refs.mainObject];
      if (!objectExists) {
        const pos = findObjectPosition(query, refs.mainObject, model);
        if (pos) {
          const suggestions = findSimilar(refs.mainObject, Object.keys(metadata.objects || {}));
          markers.push({
            severity: 8,
            startLineNumber: pos.line,
            startColumn: pos.startColumn,
            endLineNumber: pos.line,
            endColumn: pos.endColumn,
            message: `Unknown object '${refs.mainObject}'${suggestions.length ? `. Did you mean: ${suggestions.join(', ')}?` : ''}`,
            source: 'SOQL Validator',
          });
        }
      }
    }

    // Validate simple fields
    if (refs.mainObject && metadata.objects?.[refs.mainObject]) {
      const validFields = getFieldNames(metadata.objects[refs.mainObject]);

      for (const fieldName of refs.fields) {
        if (!validFields.includes(fieldName.toLowerCase())) {
          const pos = findFieldPosition(query, fieldName, model);
          if (pos) {
            const suggestions = findSimilar(fieldName, validFields);
            markers.push({
              severity: 8,
              startLineNumber: pos.line,
              startColumn: pos.startColumn,
              endLineNumber: pos.line,
              endColumn: pos.endColumn,
              message: `Unknown field '${fieldName}' on object '${refs.mainObject}'${suggestions.length ? `. Did you mean: ${suggestions.join(', ')}?` : ''}`,
              source: 'SOQL Validator',
            });
          }
        }
      }
    }

    // Validate relationship traversals
    for (const parts of refs.relationships) {
      const validationResult = validateRelationship(parts, refs.mainObject, metadata);
      if (!validationResult.valid) {
        const fullPath = parts.join('.');
        const pos = findFieldPosition(query, fullPath, model);
        if (pos) {
          markers.push({
            severity: 8,
            startLineNumber: pos.line,
            startColumn: pos.startColumn,
            endLineNumber: pos.line,
            endColumn: pos.endColumn,
            message: validationResult.error,
            source: 'SOQL Validator',
          });
        }
      }
    }
  } catch (e) {
    // Ignore semantic validation errors
    console.warn('Semantic validation error:', e);
  }

  return markers;
}

/**
 * Validate a relationship traversal path.
 */
function validateRelationship(parts, mainObject, metadata) {
  let currentObject = mainObject;
  const lookups = metadata.lookupRelationships || [];

  for (let i = 0; i < parts.length - 1; i++) {
    const relName = parts[i];

    // Skip if this is the main object name
    if (i === 0 && relName.toLowerCase() === mainObject?.toLowerCase()) {
      continue;
    }

    // Find lookup relationship
    const lookup = lookups.find(
      l => l.sourceObject === currentObject &&
           l.relationshipName?.toLowerCase() === relName.toLowerCase()
    );

    if (!lookup) {
      return {
        valid: false,
        error: `Unknown relationship '${relName}' on object '${currentObject}'`,
      };
    }

    currentObject = lookup.referenceTo;
  }

  // Validate final field
  const fieldName = parts[parts.length - 1];
  const objMeta = metadata.objects?.[currentObject];
  if (objMeta) {
    const validFields = getFieldNames(objMeta);
    if (!validFields.includes(fieldName.toLowerCase())) {
      const suggestions = findSimilar(fieldName, validFields);
      return {
        valid: false,
        error: `Unknown field '${fieldName}' on object '${currentObject}'${suggestions.length ? `. Did you mean: ${suggestions.join(', ')}?` : ''}`,
      };
    }
  }

  return { valid: true };
}

/**
 * Get field names from object metadata.
 */
function getFieldNames(objMeta) {
  const fields = objMeta?.fields || [];
  return fields.map(f => (typeof f === 'string' ? f : f.name).toLowerCase());
}

/**
 * Find position of object name in query.
 */
function findObjectPosition(query, objectName, model) {
  // Look for FROM <objectName>
  const regex = new RegExp(`\\bFROM\\s+(${objectName})\\b`, 'i');
  const match = query.match(regex);
  if (match) {
    const objectStart = match.index + match[0].indexOf(objectName);
    const pos = model.getPositionAt(objectStart);
    return {
      line: pos.lineNumber,
      startColumn: pos.column,
      endColumn: pos.column + objectName.length,
    };
  }
  return null;
}

/**
 * Find position of field name in query.
 */
function findFieldPosition(query, fieldName, model) {
  const regex = new RegExp(`\\b(${fieldName.replace(/\./g, '\\.')})\\b`, 'i');
  const match = query.match(regex);
  if (match) {
    const pos = model.getPositionAt(match.index);
    return {
      line: pos.lineNumber,
      startColumn: pos.column,
      endColumn: pos.column + fieldName.length,
    };
  }
  return null;
}

/**
 * Find similar strings using Levenshtein distance.
 */
function findSimilar(target, candidates, maxResults = 3) {
  if (!target || candidates.length === 0) return [];

  const maxDistance = Math.floor(target.length / 2) + 1;

  return candidates
    .map(c => ({
      value: c,
      distance: levenshtein(target.toLowerCase(), c.toLowerCase()),
    }))
    .filter(s => s.distance <= maxDistance)
    .sort((a, b) => a.distance - b.distance)
    .slice(0, maxResults)
    .map(s => s.value);
}

/**
 * Levenshtein distance for fuzzy matching.
 */
function levenshtein(a, b) {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix = [];
  for (let i = 0; i <= a.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= b.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }

  return matrix[a.length][b.length];
}

/**
 * Create a debounced validation function for real-time updates.
 */
export function createDebouncedValidator(monaco, editor, metadata, debounceMs = 500) {
  let timeout = null;
  const model = editor.getModel();

  return function validate() {
    if (timeout) clearTimeout(timeout);

    timeout = setTimeout(() => {
      const markers = validateSOQL(model, metadata);
      monaco.editor.setModelMarkers(model, 'soql', markers);
    }, debounceMs);
  };
}

export default {
  validateSOQL,
  createDebouncedValidator,
};
